import { useMemo, useState } from 'react';
import { FaDownload, FaFolderOpen, FaSpinner } from 'react-icons/fa';
import { Select } from '../../../components/forms/Select';
import { DOCUMENT_TYPE_META } from '../constants';
import { usePasStore } from '../pasStore';
import type { DocumentType, Policy, PolicyDocument } from '../types';
import { formatDate, relativeTime, triggerUrlDownload } from '../utils';
import { Button } from '../components/Button';
import { DataTable, type Column } from '../components/DataTable';
import { PdfPreview } from '../components/PdfPreview';
import { SlideOver } from '../components/SlideOver';
import { DocumentTypeBadge } from '../components/StatusBadges';
import { PageHeader } from '../components/primitives';

interface DocRow {
  doc: PolicyDocument;
  policy: Policy;
}

export const DocumentsPage: React.FC = () => {
  const policies = usePasStore((s) => s.policies);
  const loading = usePasStore((s) => s.loading);
  const [type, setType] = useState<string>('');
  const [preview, setPreview] = useState<DocRow | null>(null);

  const rows = useMemo<DocRow[]>(
    () =>
      policies
        .flatMap((policy) => policy.documents.map((doc) => ({ doc, policy })))
        .filter((row) => !type || row.doc.type === type)
        .sort((a, b) => b.doc.generatedAt.localeCompare(a.doc.generatedAt)),
    [policies, type],
  );

  const columns: Column<DocRow>[] = [
    {
      key: 'name',
      header: 'Document',
      primary: true,
      sortValue: (r) => r.doc.name,
      accessor: (r) => (
        <div>
          <span>{r.doc.name}</span>
          <span className="block text-xs font-normal" style={{ color: 'var(--color-text-secondary)' }}>{r.policy.policyNumber} · {r.policy.insured.name}</span>
        </div>
      ),
    },
    { key: 'type', header: 'Type', sortValue: (r) => DOCUMENT_TYPE_META[r.doc.type].label, accessor: (r) => <DocumentTypeBadge type={r.doc.type} /> },
    { key: 'version', header: 'Version', align: 'center', sortValue: (r) => r.doc.version, accessor: (r) => <span className="text-sm">v{r.doc.version}</span> },
    { key: 'size', header: 'Size', align: 'right', sortValue: (r) => r.doc.sizeKb, accessor: (r) => <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{r.doc.sizeKb} KB</span> },
    { key: 'generated', header: 'Generated', align: 'right', sortValue: (r) => r.doc.generatedAt, accessor: (r) => <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{relativeTime(r.doc.generatedAt)}</span> },
    {
      key: 'download',
      header: '',
      align: 'right',
      accessor: (r) => {
        const ready = !!r.doc.downloadUrl;
        return (
          <button
            type="button"
            disabled={!ready}
            onClick={() => r.doc.downloadUrl && triggerUrlDownload(r.doc.downloadUrl)}
            aria-label={ready ? `Download ${r.doc.name}` : `${r.doc.name} is generating`}
            title={ready ? 'Download' : 'Generating…'}
            className="rounded p-1.5 hover:bg-[var(--color-hover-background)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {ready ? <FaDownload className="h-3.5 w-3.5" /> : <FaSpinner className="h-3.5 w-3.5 animate-spin" />}
          </button>
        );
      },
    },
  ];

  const typeOptions = [
    { value: '', label: 'All document types' },
    ...(Object.keys(DOCUMENT_TYPE_META) as DocumentType[]).map((t) => ({ value: t, label: DOCUMENT_TYPE_META[t].label })),
  ];

  return (
    <div>
      <PageHeader title="Documents" subtitle={`${rows.length} documents across the book of business`} />

      <DataTable
        columns={columns}
        data={rows}
        getRowId={(r) => r.doc.id}
        onRowClick={(r) => setPreview(r)}
        loading={loading}
        searchable
        searchPlaceholder="Search documents…"
        searchText={(r) => `${r.doc.name} ${r.policy.policyNumber} ${r.policy.insured.name} ${DOCUMENT_TYPE_META[r.doc.type].label}`}
        initialSort={{ key: 'generated', direction: 'desc' }}
        emptyIcon={<FaFolderOpen />}
        emptyTitle="No documents found"
        toolbar={<Select className="!py-1.5" options={typeOptions} value={type} onChange={(e) => setType(e.target.value)} aria-label="Filter by document type" />}
      />

      <SlideOver
        isOpen={!!preview}
        onClose={() => setPreview(null)}
        title={preview ? DOCUMENT_TYPE_META[preview.doc.type].label : 'Document'}
        subtitle={preview?.doc.name}
        width="xl"
        footer={
          preview &&
          (preview.doc.downloadUrl ? (
            <Button leftIcon={<FaDownload className="h-3 w-3" />} onClick={() => triggerUrlDownload(preview.doc.downloadUrl!)}>
              Download
            </Button>
          ) : (
            <Button disabled leftIcon={<FaSpinner className="h-3 w-3 animate-spin" />}>
              Generating…
            </Button>
          ))
        }
      >
        {preview && (
          <div className="space-y-5">
            <PdfPreview doc={preview.doc} policy={preview.policy} />
            <div>
              <h4 className="mb-2 text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>Version history</h4>
              <ul className="space-y-2">
                {preview.doc.versions.map((v) => (
                  <li key={v.version} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2" style={{ borderColor: 'var(--color-border-light)' }}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>v{v.version} · <span className="capitalize font-normal">{v.status}</span></p>
                      <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{v.note}</p>
                    </div>
                    <span className="flex-shrink-0 text-xs" style={{ color: 'var(--color-text-secondary)' }}>{formatDate(v.generatedAt)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </SlideOver>
    </div>
  );
};
